import type {
    CompRef,
    IdMap,
    ImagesInfoMap,
    Pointer,
    UpdateComponentBuilderImageParams
} from '@wix/document-services-types'

import {
    getNextIdForPrefix,
    getSectionNameByPointer,
    ignoredTemplateOutlineTypes,
    type PointerPredicate
} from './aiExtensionContent'
import type {CreateExtArgs, EnvironmentContext} from '@wix/document-manager-core'
import type {StructureExtensionAPI} from '../structure'
import type {ComponentDefinitionExtensionAPI} from '../componentDefinition'
import {fetchImageUrlsForBuilder} from './aiExtensionUtils'
import {DATA_TYPES} from '../../constants/constants'
import {handleAiError, predicateBuilderComp} from './aiBuilderUtils'
import type {ManifestExtensionAPI} from '../builderComponents/manifest'
import type {ViewerExtensionAPI} from '../viewer/viewerExtension'
import {processGeneratedImages} from './images/utils'
import _ from 'lodash'
import type {LoggerExtAPI} from '../logger'
import {ERRORS, LOGS} from './constants'

const caller = 'generateImages'

export interface BuilderImageOutlineDataWithIdMapAndOldImageInfoMap {
    outline: BuilderImageOutlineData
    idMap: IdMap
    oldImagesInfo: ImagesInfoMap
}

export type ImagePredicate = (pointer: Pointer) => boolean

interface BuilderImageSectionData {
    images: {id: string; width: string; height: string}[]
}

interface BuilderTextSectionData {
    [componentName: string]: Record<string, string>
}

type SectionData = BuilderImageSectionData & BuilderTextSectionData

export interface BuilderImageOutlineData {
    [section: string]: SectionData
}

export const getBuilderImageOutlineData = (
    createExtArgs: CreateExtArgs,
    shouldAddImagePredicate: ImagePredicate,
    pagePointer: Pointer
): BuilderImageOutlineDataWithIdMapAndOldImageInfoMap => {
    const {pointers} = createExtArgs
    const {components} = createExtArgs.extensionAPI as StructureExtensionAPI
    const {componentDefinition} = createExtArgs.extensionAPI as ComponentDefinitionExtensionAPI
    const {manifest} = createExtArgs.extensionAPI as ManifestExtensionAPI
    const {viewer} = createExtArgs.extensionAPI as ViewerExtensionAPI

    const predicateBuilder = (pointer: Pointer): boolean => {
        const componentType = components.getComponentType(pointer)
        return predicateBuilderComp(createExtArgs.extensionAPI, componentType)
    }

    const getContentRecursively = (
        rootPointer: Pointer,
        shouldAddImage: ImagePredicate,
        idMap: IdMap,
        oldImagesInfo: ImagesInfoMap,
        typeCounter: Record<string, number>,
        sectionName: string | undefined,
        predicate: PointerPredicate,
        outline: any
    ): void => {
        const type = components.getComponentType(rootPointer)
        const toAdd = shouldAddImage(rootPointer)

        if (ignoredTemplateOutlineTypes.has(type)) {
            return
        }

        if (componentDefinition.isContainer(type)) {
            if (componentDefinition.isSection(type)) {
                const sectionNameByPointer = getSectionNameByPointer(
                    createExtArgs.extensionAPI,
                    rootPointer,
                    DATA_TYPES.anchors
                )
                sectionName = getNextIdForPrefix(sectionNameByPointer, typeCounter)
            }
            const children = pointers.structure.getChildren(rootPointer)
            for (const childPointer of children) {
                getContentRecursively(
                    childPointer,
                    shouldAddImage,
                    idMap,
                    oldImagesInfo,
                    typeCounter,
                    sectionName,
                    predicate,
                    outline
                )
            }
        }
        if (!sectionName) return
        if (predicate(rootPointer)) {
            outline[sectionName] ??= {
                images: []
            }
            const textContextProps = manifest.getData(rootPointer, true, [{prop: 'text'}])
            textContextProps.forEach(context => {
                const {value: data} = context
                const mappedId = getNextIdForPrefix(context.type, typeCounter)
                return (outline[sectionName][mappedId] = data)
            })

            if (toAdd) {
                const imageContextProps = manifest.getData(rootPointer, true, [{prop: 'image'}])
                imageContextProps.forEach(context => {
                    const {prop, path, nestedDataPath, value: data} = context
                    const mappedId = getNextIdForPrefix(context.type, typeCounter)
                    const measurements =
                        path !== undefined
                            ? viewer.getInnerElementMeasurements(rootPointer as CompRef, path)
                            : viewer.getComponentMeasurements(rootPointer as CompRef)
                    idMap[mappedId] = {pointer: rootPointer, prop, path, nestedDataPath}
                    oldImagesInfo[mappedId] = {imageUri: data?.uri, width: data?.width, height: data?.height}
                    const {width, height} = measurements
                    outline[sectionName].images.push({id: mappedId, width, height})
                })
            }
        }
    }

    const outline: BuilderImageOutlineData = {}
    const typeCounter: Record<string, number> = {}
    const idMap = {}
    const oldImagesInfo = {}
    getContentRecursively(
        pagePointer,
        shouldAddImagePredicate,
        idMap,
        oldImagesInfo,
        typeCounter,
        undefined,
        predicateBuilder,
        outline
    )
    const outlineOfSectionsWithImages = Object.fromEntries(
        Object.entries(outline).filter(([, val]) => val.images.length > 0)
    )
    return {outline: outlineOfSectionsWithImages, idMap, oldImagesInfo}
}

export const getBuilderImageOutlineDataForInjection = (
    createExtArgs: CreateExtArgs,
    pointers: Pointer[]
): BuilderImageOutlineDataWithIdMapAndOldImageInfoMap => {
    const pagePointer = createExtArgs.pointers.structure.getPageOfComponent(pointers[0])
    const shouldAddImageInInjection =
        (targetPointers: Pointer[]): ImagePredicate =>
        (pointer: Pointer) =>
            targetPointers.some(targetPointer => pointer.id === targetPointer.id)
    return getBuilderImageOutlineData(createExtArgs, shouldAddImageInInjection(pointers), pagePointer)
}

export async function getImageInjectionForBuilder(
    {serverFacade}: EnvironmentContext,
    createExtArgs: CreateExtArgs,
    {
        compRefsWithHints,
        siteType,
        businessName,
        siteDescription,
        photoTheme,
        userRequestInfo,
        onSuccess,
        onError
    }: UpdateComponentBuilderImageParams
): Promise<void> {
    const {extensionAPI} = createExtArgs
    const {logger} = extensionAPI as LoggerExtAPI
    logger.interactionStarted(caller, {tags: {caller}})

    const targetPointers: Pointer[] = compRefsWithHints.map(({compRef}) => compRef)

    const {outline, idMap, oldImagesInfo} = getBuilderImageOutlineDataForInjection(createExtArgs, targetPointers)
    const msid = createExtArgs.dal.get(createExtArgs.pointers.rendererModel.getMetaSiteId())

    const compIdToHints = _.fromPairs(compRefsWithHints.map(({compRef, hints}) => [compRef.id, [...hints]]))
    const defaultHint = compRefsWithHints[0]?.hints?.[0]
    const imagesWithHints = _.mapValues(idMap, value => compIdToHints[value.pointer.id]?.shift() ?? defaultHint)

    try {
        logger.interactionStarted(LOGS.IMAGES.GENERATION, {
            tags: {caller},
            extras: {imagesAmount: Object.keys(idMap).length}
        })
        const res = await fetchImageUrlsForBuilder(
            serverFacade,
            outline,
            imagesWithHints,
            siteType,
            businessName,
            siteDescription,
            photoTheme,
            msid,
            userRequestInfo
        )
        const imagesInfo = JSON.parse(res)
        logger.interactionEnded(LOGS.IMAGES.GENERATION, {tags: {caller}})

        await processGeneratedImages(createExtArgs, imagesInfo, idMap, oldImagesInfo, onSuccess, onError, caller)
    } catch (e: any) {
        handleAiError(e, ERRORS.IMAGES.GENERATION, logger, onError, {caller})
    }
}
